"use client"

import { useState } from "react"
import { Plus, Trash2, ChevronDown, ChevronUp } from "lucide-react"
import type { GameConfig, Section, Step } from "../AdminApp"
import "../styles/EditorPanel.css"

interface EditorPanelProps {
  gameConfig: GameConfig
  setGameConfig: (config: GameConfig) => void
  sections: Section[]
  setSections: (sections: Section[]) => void
  selectedStep: { sectionIndex: number; stepIndex: number } | null
  setSelectedStep: (step: { sectionIndex: number; stepIndex: number } | null) => void
  currentSectionIndex: number
  setCurrentSectionIndex: (index: number) => void
  currentStepIndex: number
  setCurrentStepIndex: (index: number) => void
  lessonId: string
  language: string
}

export default function EditorPanel({
  gameConfig,
  setGameConfig,
  sections,
  setSections,
  selectedStep,
  currentSectionIndex,
  setCurrentSectionIndex,
  currentStepIndex,
  setCurrentStepIndex,
  lessonId,
  language,
}: EditorPanelProps) {
  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set([0]))
  const [uploadingAudio, setUploadingAudio] = useState(false)
  const [uploadingSubtitle, setUploadingSubtitle] = useState(false)
  const [uploadingVideo, setUploadingVideo] = useState(false)
  const [uploadingIcon, setUploadingIcon] = useState(false)
  const [uploadingImage, setUploadingImage] = useState(false)
  const [showLessonAvatarSettings, setShowLessonAvatarSettings] = useState(false)
  const [expandedSectionAvatarSettings, setExpandedSectionAvatarSettings] = useState<Set<number>>(new Set())
  const [bellaPreset, setBellaPreset] = useState<string>("custom")
  const [sectionBellaPresets, setSectionBellaPresets] = useState<Map<number, string>>(new Map())

  const currentSection = sections[currentSectionIndex]
  const currentStep = currentSection?.steps[currentStepIndex]

  const applyBellaPreset = (preset: string) => {
    setBellaPreset(preset)

    if (preset === "right") {
      setGameConfig({
        ...gameConfig,
        avatar: "bella",
        avatarAlignment: undefined,
        right: "5rem",
        bottom: "-1rem",
        top: undefined,
        left: undefined,
        avatarScale: 1.5,
        mobileScale: 1.3,
        mobileRight: undefined,
        mobileBottom: "1rem",
      })
    } else if (preset === "centered") {
      setGameConfig({
        ...gameConfig,
        avatar: "bella",
        avatarAlignment: "centered",
        bottom: "-2rem",
        top: undefined,
        left: undefined,
        right: undefined,
        avatarScale: 2.2,
        mobileScale: 1.4,
        mobileRight: undefined,
        mobileBottom: undefined,
      })
    }
    // For "custom", don't change any values - user will set them manually
  }

  const applySectionBellaPreset = (sectionIndex: number, preset: string) => {
    const newPresets = new Map(sectionBellaPresets)
    newPresets.set(sectionIndex, preset)
    setSectionBellaPresets(newPresets)

    const newSections = [...sections]
    if (!newSections[sectionIndex].config) {
      newSections[sectionIndex].config = {}
    }

    if (preset === "right") {
      newSections[sectionIndex].config = {
        ...newSections[sectionIndex].config,
        avatar: "bella",
        avatarAlignment: undefined,
        right: "5rem",
        bottom: "-1rem",
        top: undefined,
        left: undefined,
        avatarScale: 1.5,
        mobileScale: 1.3,
        mobileRight: undefined,
        mobileBottom: "1rem",
      }
    } else if (preset === "centered") {
      newSections[sectionIndex].config = {
        ...newSections[sectionIndex].config,
        avatar: "bella",
        avatarAlignment: "centered",
        bottom: "-2rem",
        top: undefined,
        left: undefined,
        right: undefined,
        avatarScale: 2.2,
        mobileScale: 1.4,
        mobileBottom: undefined,
      }
    }

    setSections(newSections)
  }

  const toggleSection = (index: number) => {
    const newExpanded = new Set(expandedSections)
    if (newExpanded.has(index)) {
      newExpanded.delete(index)
    } else {
      newExpanded.add(index)
    }
    setExpandedSections(newExpanded)
  }

  const updateSectionTitle = (sectionIndex: number, newTitle: string) => {
    const newSections = [...sections]
    newSections[sectionIndex].title = newTitle
    setSections(newSections)
  }

  const updateSectionEmoji = (sectionIndex: number, newEmoji: string) => {
    const newSections = [...sections]
    newSections[sectionIndex].emoji = newEmoji
    setSections(newSections)
  }

  const addSection = () => {
    setSections([
      ...sections,
      {
        key: `section-${sections.length + 1}`,
        title: "New Section",
        emoji: "📚",
        steps: [],
        achievements: [],
      },
    ])
  }

  const addStep = (sectionIndex: number, type: string) => {
    const newSections = [...sections]
    const newStep: Step =
      type === "quiz"
        ? {
            type: "quiz",
            content: {
              title: "New Quiz Question",
              description: "",
              options: [
                { text: "Option A", correct: true, feedback: "Correct!" },
                { text: "Option B", correct: false, feedback: "Try again" },
              ],
            },
          }
        : type === "celebration"
          ? {
              type: "celebration",
              content: {
                title: "Well Done!",
                description: "You completed this section!",
              },
            }
          : type === "video"
            ? {
                type: "intro",
                content: {
                  title: "New Video",
                  description: "Add your video content here...",
                  fullscreenContent: true,
                  fullHeightVideo: true,
                },
              }
            : {
                type: "story",
                content: {
                  title: "New Step",
                  description: "Add your content here...",
                },
              }

    newSections[sectionIndex].steps.push(newStep)
    setSections(newSections)
    setCurrentSectionIndex(sectionIndex)
    setCurrentStepIndex(newSections[sectionIndex].steps.length - 1)
  }

  const deleteSection = (index: number) => {
    if (window.confirm("Delete this section?")) {
      setSections(sections.filter((_, i) => i !== index))
      if (currentSectionIndex >= sections.length - 1) {
        setCurrentSectionIndex(Math.max(0, sections.length - 2))
      }
    }
  }

  const deleteStep = (sectionIndex: number, stepIndex: number) => {
    if (window.confirm("Delete this step?")) {
      const newSections = [...sections]
      newSections[sectionIndex].steps = newSections[sectionIndex].steps.filter((_, i) => i !== stepIndex)
      setSections(newSections)
      if (currentStepIndex >= newSections[sectionIndex].steps.length) {
        setCurrentStepIndex(Math.max(0, newSections[sectionIndex].steps.length - 1))
      }
    }
  }

  const updateStep = (sectionIndex: number, stepIndex: number, path: string, value: any) => {
    const newSections = [...sections]
    const keys = path.split(".")
    let target: any = newSections[sectionIndex].steps[stepIndex]

    for (let i = 0; i < keys.length - 1; i++) {
      target = target[keys[i]]
    }
    target[keys[keys.length - 1]] = value
    setSections(newSections)
  }

  const handleFileUploadForStep = async (
    file: File,
    fileType: "audio" | "subtitle" | "video",
    sectionIndex: number,
    stepIndex: number,
  ) => {
    if (!lessonId || !language) {
      alert("Please enter a Lesson ID and save the lesson first before uploading files")
      return
    }

    if (!lessonId.trim()) {
      alert("Lesson ID cannot be empty. Please enter a valid Lesson ID.")
      return
    }

    const formData = new FormData()
    formData.append("file", file)
    formData.append("lessonId", lessonId)
    formData.append("language", language)
    formData.append("fileType", fileType)

    try {
      if (fileType === "audio") {
        setUploadingAudio(true)
      } else if (fileType === "subtitle") {
        setUploadingSubtitle(true)
      } else if (fileType === "video") {
        setUploadingVideo(true)
      }

      const response = await fetch("/portal/lesson-admin-api.php?action=upload", {
        method: "POST",
        body: formData,
      })

      const result = await response.json()

      if (result.success) {
        if (fileType === "audio") {
          updateStep(sectionIndex, stepIndex, "content.audioSrc", result.path)
        } else if (fileType === "subtitle") {
          updateStep(sectionIndex, stepIndex, "content.srtSrc", result.path)
        } else if (fileType === "video") {
          updateStep(sectionIndex, stepIndex, "content.videoUrl", result.path)
          updateStep(sectionIndex, stepIndex, "content.fullscreenContent", true)
          updateStep(sectionIndex, stepIndex, "content.fullHeightVideo", true)
        }
        alert("File uploaded successfully!")
      } else {
        alert("Upload failed: " + result.error)
      }
    } catch (error) {
      alert("Upload failed: " + error)
    } finally {
      if (fileType === "audio") {
        setUploadingAudio(false)
      } else if (fileType === "subtitle") {
        setUploadingSubtitle(false)
      } else if (fileType === "video") {
        setUploadingVideo(false)
      }
    }
  }

  const handleFileUpload = async (file: File, fileType: "audio" | "subtitle" | "video") => {
    return handleFileUploadForStep(file, fileType, currentSectionIndex, currentStepIndex)
  }

  const updateSectionConfig = (sectionIndex: number, key: string, value: any) => {
    const newSections = [...sections]
    if (!newSections[sectionIndex].config) {
      newSections[sectionIndex].config = {}
    }
    newSections[sectionIndex].config![key] = value
    setSections(newSections)
  }

  const toggleSectionAvatarSettings = (sectionIndex: number) => {
    const newExpanded = new Set(expandedSectionAvatarSettings)
    if (newExpanded.has(sectionIndex)) {
      newExpanded.delete(sectionIndex)
    } else {
      newExpanded.add(sectionIndex)
    }
    setExpandedSectionAvatarSettings(newExpanded)
  }

  const handleIconUpload = async (file: File) => {
    if (!lessonId || !language) {
      alert("Please enter a Lesson ID and save the lesson first before uploading files")
      return
    }

    if (!lessonId.trim()) {
      alert("Lesson ID cannot be empty. Please enter a valid Lesson ID.")
      return
    }

    const formData = new FormData()
    formData.append("file", file)
    formData.append("lessonId", lessonId)
    formData.append("language", language)
    formData.append("fileType", "image")

    try {
      setUploadingIcon(true)

      const response = await fetch("/portal/lesson-admin-api.php?action=upload", {
        method: "POST",
        body: formData,
      })

      const result = await response.json()

      if (result.success) {
        setGameConfig({ ...gameConfig, iconImage: result.path })
        alert("Icon image uploaded successfully!")
      } else {
        alert("Upload failed: " + result.error)
      }
    } catch (error) {
      alert("Upload failed: " + error)
    } finally {
      setUploadingIcon(false)
    }
  }

  const handleStepImageUpload = async (file: File, sectionIndex: number, stepIndex: number) => {
    if (!lessonId || !language) {
      alert("Please enter a Lesson ID and save the lesson first before uploading files")
      return
    }

    const formData = new FormData()
    formData.append("file", file)
    formData.append("lessonId", lessonId)
    formData.append("language", language)
    formData.append("fileType", "image")

    try {
      setUploadingImage(true)

      const response = await fetch("/portal/lesson-admin-api.php?action=upload", {
        method: "POST",
        body: formData,
      })

      const result = await response.json()

      if (result.success) {
        updateStep(sectionIndex, stepIndex, "content.image", result.path)
        alert("Image uploaded successfully!")
      } else {
        alert("Upload failed: " + result.error)
      }
    } catch (error) {
      alert("Upload failed: " + error)
    } finally {
      setUploadingImage(false)
    }
  }

  const addColumn = (sectionIndex: number, stepIndex: number) => {
    const newSections = [...sections]
    if (!newSections[sectionIndex].steps[stepIndex].content.columns) {
      newSections[sectionIndex].steps[stepIndex].content.columns = []
    }
    newSections[sectionIndex].steps[stepIndex].content.columns!.push({
      listItems: [],
    })
    setSections(newSections)
  }

  const deleteColumn = (sectionIndex: number, stepIndex: number, columnIndex: number) => {
    const newSections = [...sections]
    newSections[sectionIndex].steps[stepIndex].content.columns = newSections[sectionIndex].steps[
      stepIndex
    ].content.columns!.filter((_, i) => i !== columnIndex)
    setSections(newSections)
  }

  const updateColumnField = (
    sectionIndex: number,
    stepIndex: number,
    columnIndex: number,
    field: string,
    value: any,
  ) => {
    const newSections = [...sections]
    const column = newSections[sectionIndex].steps[stepIndex].content.columns![columnIndex]
    ;(column as any)[field] = value
    setSections(newSections)
  }

  const addListItem = (sectionIndex: number, stepIndex: number, columnIndex: number) => {
    const newSections = [...sections]
    const column = newSections[sectionIndex].steps[stepIndex].content.columns![columnIndex]
    if (!column.listItems) {
      column.listItems = []
    }
    column.listItems.push({ text: "" })
    setSections(newSections)
  }

  const updateListItem = (
    sectionIndex: number,
    stepIndex: number,
    columnIndex: number,
    itemIndex: number,
    field: "text" | "highlightAtSrtIndex",
    value: string | number | undefined,
  ) => {
    const newSections = [...sections]
    const column = newSections[sectionIndex].steps[stepIndex].content.columns![columnIndex]
    const item = column.listItems![itemIndex]

    if (field === "text") {
      item.text = value as string
    } else if (field === "highlightAtSrtIndex") {
      item.highlightAtSrtIndex = value as number | undefined
    }

    setSections(newSections)
  }

  const deleteListItem = (sectionIndex: number, stepIndex: number, columnIndex: number, itemIndex: number) => {
    const newSections = [...sections]
    const column = newSections[sectionIndex].steps[stepIndex].content.columns![columnIndex]
    column.listItems = column.listItems!.filter((_, i) => i !== itemIndex)
    setSections(newSections)
  }

  const addLink = (sectionIndex: number, stepIndex: number) => {
    const newSections = [...sections]
    if (!newSections[sectionIndex].steps[stepIndex].content.links) {
      newSections[sectionIndex].steps[stepIndex].content.links = []
    }
    newSections[sectionIndex].steps[stepIndex].content.links!.push({ text: "", url: "" })
    setSections(newSections)
  }

  const updateLink = (
    sectionIndex: number,
    stepIndex: number,
    linkIndex: number,
    field: "text" | "url",
    value: string,
  ) => {
    const newSections = [...sections]
    newSections[sectionIndex].steps[stepIndex].content.links![linkIndex][field] = value
    setSections(newSections)
  }

  const deleteLink = (sectionIndex: number, stepIndex: number, linkIndex: number) => {
    const newSections = [...sections]
    newSections[sectionIndex].steps[stepIndex].content.links = newSections[sectionIndex].steps[
      stepIndex
    ].content.links!.filter((_, i) => i !== linkIndex)
    setSections(newSections)
  }

  const addObservation = (sectionIndex: number, stepIndex: number) => {
    const newSections = [...sections]
    if (!newSections[sectionIndex].steps[stepIndex].content.tools) {
      newSections[sectionIndex].steps[stepIndex].content.tools = [{ observations: [] }]
    }
    if (!newSections[sectionIndex].steps[stepIndex].content.tools![0].observations) {
      newSections[sectionIndex].steps[stepIndex].content.tools![0].observations = []
    }
    newSections[sectionIndex].steps[stepIndex].content.tools![0].observations!.push({ text: "" })
    setSections(newSections)
  }

  const updateObservation = (sectionIndex: number, stepIndex: number, observationIndex: number, text: string) => {
    const newSections = [...sections]
    newSections[sectionIndex].steps[stepIndex].content.tools![0].observations![observationIndex].text = text
    setSections(newSections)
  }

  const deleteObservation = (sectionIndex: number, stepIndex: number, observationIndex: number) => {
    const newSections = [...sections]
    newSections[sectionIndex].steps[stepIndex].content.tools![0].observations = newSections[sectionIndex].steps[
      stepIndex
    ].content.tools![0].observations!.filter((_, i) => i !== observationIndex)
    setSections(newSections)
  }

  const addTip = (sectionIndex: number, stepIndex: number) => {
    const newSections = [...sections]
    if (!newSections[sectionIndex].steps[stepIndex].content.tips) {
      newSections[sectionIndex].steps[stepIndex].content.tips = []
    }
    newSections[sectionIndex].steps[stepIndex].content.tips!.push({ icon: "💡", text: "" })
    setSections(newSections)
  }

  const updateTip = (
    sectionIndex: number,
    stepIndex: number,
    tipIndex: number,
    field: "icon" | "text",
    value: string,
  ) => {
    const newSections = [...sections]
    newSections[sectionIndex].steps[stepIndex].content.tips![tipIndex][field] = value
    setSections(newSections)
  }

  const deleteTip = (sectionIndex: number, stepIndex: number, tipIndex: number) => {
    const newSections = [...sections]
    newSections[sectionIndex].steps[stepIndex].content.tips = newSections[sectionIndex].steps[
      stepIndex
    ].content.tips!.filter((_, i) => i !== tipIndex)
    setSections(newSections)
  }

  const renderStepEditor = (step: Step, sectionIndex: number, stepIndex: number) => {
    return (
      <div className="step-editor-inline">
        <div className="editor-field">
          <label>Step Type</label>
          <div className="step-type-badge">{step.type}</div>
        </div>
        <div className="editor-field">
          <label>Title</label>
          <input
            className="input"
            value={step.content.title}
            onChange={(e) => updateStep(sectionIndex, stepIndex, "content.title", e.target.value)}
          />
        </div>
        <div className="editor-field">
          <label>Description</label>
          <textarea
            className="textarea"
            value={step.content.description}
            onChange={(e) => updateStep(sectionIndex, stepIndex, "content.description", e.target.value)}
            rows={4}
          />
        </div>

        <div className="editor-field">
          <label>Video File (MP4/WebM)</label>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <input
              className="input"
              value={step.content.videoUrl || ""}
              onChange={(e) => updateStep(sectionIndex, stepIndex, "content.videoUrl", e.target.value)}
              placeholder="/content-beta/nl/lesson-id/videos/video.mp4"
              style={{ flex: 1 }}
            />
            <label className="btn-sm btn-secondary" style={{ cursor: "pointer", margin: 0 }}>
              {uploadingVideo ? "Uploading..." : "Upload Video"}
              <input
                type="file"
                accept=".mp4,.webm"
                style={{ display: "none" }}
                disabled={uploadingVideo}
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) {
                    handleFileUploadForStep(file, "video", sectionIndex, stepIndex)
                  }
                }}
              />
            </label>
          </div>
          {step.content.videoUrl && (
            <div style={{ marginTop: "8px", fontSize: "12px", color: "#666" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <input
                  type="checkbox"
                  checked={step.content.fullscreenContent || false}
                  onChange={(e) => updateStep(sectionIndex, stepIndex, "content.fullscreenContent", e.target.checked)}
                />
                Fullscreen Content
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "4px" }}>
                <input
                  type="checkbox"
                  checked={step.content.fullHeightVideo || false}
                  onChange={(e) => updateStep(sectionIndex, stepIndex, "content.fullHeightVideo", e.target.checked)}
                />
                Full Height Video
              </label>
            </div>
          )}
        </div>

        <div className="editor-field">
          <label>Audio File (MP3)</label>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <input
              className="input"
              value={step.content.audioSrc || ""}
              onChange={(e) => updateStep(sectionIndex, stepIndex, "content.audioSrc", e.target.value)}
              placeholder="/content-beta/nl/lesson-id/audio/audio.mp3"
              style={{ flex: 1 }}
            />
            <label className="btn-sm btn-secondary" style={{ cursor: "pointer", margin: 0 }}>
              {uploadingAudio ? "Uploading..." : "Upload MP3"}
              <input
                type="file"
                accept=".mp3"
                style={{ display: "none" }}
                disabled={uploadingAudio}
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) {
                    handleFileUploadForStep(file, "audio", sectionIndex, stepIndex)
                  }
                }}
              />
            </label>
          </div>
        </div>

        <div className="editor-field">
          <label>Subtitle File (SRT)</label>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <input
              className="input"
              value={step.content.srtSrc || ""}
              onChange={(e) => updateStep(sectionIndex, stepIndex, "content.srtSrc", e.target.value)}
              placeholder="/content-beta/nl/lesson-id/audio/subtitles.srt"
              style={{ flex: 1 }}
            />
            <label className="btn-sm btn-secondary" style={{ cursor: "pointer", margin: 0 }}>
              {uploadingSubtitle ? "Uploading..." : "Upload SRT"}
              <input
                type="file"
                accept=".srt"
                style={{ display: "none" }}
                disabled={uploadingSubtitle}
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) {
                    handleFileUploadForStep(file, "subtitle", sectionIndex, stepIndex)
                  }
                }}
              />
            </label>
          </div>
        </div>

        <div className="editor-field">
          <label>Image</label>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <input
              className="input"
              value={step.content.image || ""}
              onChange={(e) => updateStep(sectionIndex, stepIndex, "content.image", e.target.value)}
              placeholder="/content-beta/nl/lesson-id/images/image.png"
              style={{ flex: 1 }}
            />
            <label className="btn-sm btn-secondary" style={{ cursor: "pointer", margin: 0 }}>
              {uploadingImage ? "Uploading..." : "Upload Image"}
              <input
                type="file"
                accept=".png,.jpg,.jpeg,.gif,.webp"
                style={{ display: "none" }}
                disabled={uploadingImage}
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) {
                    handleStepImageUpload(file, sectionIndex, stepIndex)
                  }
                }}
              />
            </label>
          </div>
          {step.content.image && (
            <div style={{ marginTop: "8px" }}>
              <img
                src={step.content.image || "/placeholder.svg"}
                alt="Step content"
                style={{ maxWidth: "200px", maxHeight: "150px", borderRadius: "8px", border: "1px solid #ddd" }}
              />
            </div>
          )}
        </div>

        <div className="editor-field">
          <div className="editor-field-header">
            <label>Bullet List (Observations)</label>
            <button className="btn-sm btn-secondary" onClick={() => addObservation(sectionIndex, stepIndex)}>
              <Plus size={14} />
              Add Bullet
            </button>
          </div>
          {step.content.tools?.[0]?.observations && step.content.tools[0].observations.length > 0 ? (
            <div className="bullet-list-editor">
              {step.content.tools[0].observations.map((observation, observationIndex) => (
                <div key={observationIndex} className="bullet-item">
                  <span className="bullet-dot">•</span>
                  <input
                    className="input input-sm"
                    value={observation.text}
                    onChange={(e) => updateObservation(sectionIndex, stepIndex, observationIndex, e.target.value)}
                    placeholder="Bullet point text"
                    style={{ flex: 1 }}
                  />
                  <button
                    className="btn-icon"
                    onClick={() => deleteObservation(sectionIndex, stepIndex, observationIndex)}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ fontSize: "12px", color: "#666", fontStyle: "italic" }}>No bullet points yet</p>
          )}
        </div>

        <div className="editor-field">
          <div className="editor-field-header">
            <label>Tips</label>
            <button className="btn-sm btn-secondary" onClick={() => addTip(sectionIndex, stepIndex)}>
              <Plus size={14} />
              Add Tip
            </button>
          </div>
          {step.content.tips && step.content.tips.length > 0 ? (
            <div className="tips-editor">
              {step.content.tips.map((tip, tipIndex) => (
                <div key={tipIndex} className="tip-item">
                  <input
                    className="input input-sm"
                    value={tip.icon || ""}
                    onChange={(e) => updateTip(sectionIndex, stepIndex, tipIndex, "icon", e.target.value)}
                    placeholder="🔍"
                    style={{ width: "50px", textAlign: "center" }}
                    maxLength={2}
                  />
                  <input
                    className="input input-sm"
                    value={tip.text}
                    onChange={(e) => updateTip(sectionIndex, stepIndex, tipIndex, "text", e.target.value)}
                    placeholder="Tip text"
                    style={{ flex: 1 }}
                  />
                  <button className="btn-icon" onClick={() => deleteTip(sectionIndex, stepIndex, tipIndex)}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ fontSize: "12px", color: "#666", fontStyle: "italic" }}>No tips yet</p>
          )}
        </div>

        <div className="editor-field">
          <div className="editor-field-header">
            <label>Content Columns ({step.content.columns?.length || 0} columns)</label>
            <button className="btn-sm btn-secondary" onClick={() => addColumn(sectionIndex, stepIndex)}>
              <Plus size={14} />
              Add Column
            </button>
          </div>
          {step.content.columns && step.content.columns.length > 0 ? (
            <div className="columns-editor">
              {step.content.columns.map((column, columnIndex) => (
                <div key={columnIndex} className="column-editor">
                  <div className="column-header">
                    <span className="column-number">Column {columnIndex + 1}</span>
                    <button className="btn-icon" onClick={() => deleteColumn(sectionIndex, stepIndex, columnIndex)}>
                      <Trash2 size={14} />
                    </button>
                  </div>

                  <div className="column-content">
                    <div className="editor-field">
                      <label style={{ fontSize: "12px" }}>Column Type</label>
                      <select
                        className="select select-sm"
                        value={column.imageSrc !== undefined ? "image" : "text"}
                        onChange={(e) => {
                          const newSections = [...sections]
                          const col = newSections[sectionIndex].steps[stepIndex].content.columns![columnIndex]
                          if (e.target.value === "image") {
                            col.imageSrc = ""
                            col.imageAlt = ""
                            delete col.title
                            delete col.description
                          } else {
                            col.title = ""
                            col.description = ""
                            delete col.imageSrc
                            delete col.imageAlt
                          }
                          setSections(newSections)
                        }}
                      >
                        <option value="image">Image</option>
                        <option value="text">Text Content</option>
                      </select>
                    </div>

                    {/* Image */}
                    {column.imageSrc !== undefined && (
                      <>
                        <div className="editor-field">
                          <label style={{ fontSize: "12px" }}>Image Source</label>
                          <input
                            className="input input-sm"
                            value={column.imageSrc || ""}
                            onChange={(e) =>
                              updateColumnField(sectionIndex, stepIndex, columnIndex, "imageSrc", e.target.value)
                            }
                            placeholder="/content/nl/lesson-id/images/image.jpg"
                          />
                        </div>
                        <div className="editor-field">
                          <label style={{ fontSize: "12px" }}>Image Alt Text</label>
                          <input
                            className="input input-sm"
                            value={column.imageAlt || ""}
                            onChange={(e) =>
                              updateColumnField(sectionIndex, stepIndex, columnIndex, "imageAlt", e.target.value)
                            }
                            placeholder="Description of image"
                          />
                        </div>
                        {column.imageSrc && (
                          <div style={{ marginTop: "8px" }}>
                            <img
                              src={column.imageSrc || "/placeholder.svg"}
                              alt={column.imageAlt || "Column image"}
                              style={{
                                maxWidth: "150px",
                                maxHeight: "100px",
                                borderRadius: "4px",
                                border: "1px solid #ddd",
                              }}
                            />
                          </div>
                        )}
                      </>
                    )}

                    {/* Text Content */}
                    {column.title !== undefined && (
                      <>
                        <div className="editor-field">
                          <label style={{ fontSize: "12px" }}>Title</label>
                          <input
                            className="input input-sm"
                            value={column.title || ""}
                            onChange={(e) =>
                              updateColumnField(sectionIndex, stepIndex, columnIndex, "title", e.target.value)
                            }
                            placeholder="Column title"
                          />
                        </div>
                        <div className="editor-field">
                          <label style={{ fontSize: "12px" }}>Description</label>
                          <textarea
                            className="textarea"
                            value={column.description || ""}
                            onChange={(e) =>
                              updateColumnField(sectionIndex, stepIndex, columnIndex, "description", e.target.value)
                            }
                            placeholder="Column description"
                            rows={3}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ fontSize: "12px", color: "#666", fontStyle: "italic" }}>
              No columns yet. Add columns for multi-column layouts with images or text content.
            </p>
          )}
        </div>

        <div className="editor-field">
          <div className="editor-field-header">
            <label>Links</label>
            <button className="btn-sm btn-secondary" onClick={() => addLink(sectionIndex, stepIndex)}>
              <Plus size={14} />
              Add Link
            </button>
          </div>
          {step.content.links && step.content.links.length > 0 ? (
            <div className="links-editor">
              {step.content.links.map((link, linkIndex) => (
                <div key={linkIndex} className="link-item">
                  <input
                    className="input input-sm"
                    value={link.text}
                    onChange={(e) => updateLink(sectionIndex, stepIndex, linkIndex, "text", e.target.value)}
                    placeholder="Link text"
                    style={{ flex: 1 }}
                  />
                  <input
                    className="input input-sm"
                    value={link.url}
                    onChange={(e) => updateLink(sectionIndex, stepIndex, linkIndex, "url", e.target.value)}
                    placeholder="https://..."
                    style={{ flex: 1 }}
                  />
                  <button className="btn-icon" onClick={() => deleteLink(sectionIndex, stepIndex, linkIndex)}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ fontSize: "12px", color: "#666", fontStyle: "italic" }}>No links yet</p>
          )}
        </div>

        {step.type === "quiz" && (
          <div className="editor-field">
            <div className="editor-field-header">
              <label>Quiz Options</label>
              <button
                className="btn-sm btn-secondary"
                onClick={() => {
                  const newSections = [...sections]
                  if (!newSections[sectionIndex].steps[stepIndex].content.options) {
                    newSections[sectionIndex].steps[stepIndex].content.options = []
                  }
                  newSections[sectionIndex].steps[stepIndex].content.options!.push({
                    text: "New option",
                    correct: false,
                    feedback: "",
                  })
                  setSections(newSections)
                }}
              >
                <Plus size={14} />
                Add
              </button>
            </div>
            <div className="quiz-options-editor">
              {step.content.options?.map((option, optionIndex) => (
                <div key={optionIndex} className="quiz-option-editor">
                  <div className="quiz-option-header">
                    <input
                      type="checkbox"
                      checked={option.correct}
                      onChange={(e) => {
                        const newSections = [...sections]
                        newSections[sectionIndex].steps[stepIndex].content.options![optionIndex].correct =
                          e.target.checked
                        setSections(newSections)
                      }}
                    />
                    <span>Option {String.fromCharCode(65 + optionIndex)}</span>
                    <button
                      className="btn-icon"
                      onClick={() => {
                        const newSections = [...sections]
                        newSections[sectionIndex].steps[stepIndex].content.options = newSections[sectionIndex].steps[
                          stepIndex
                        ].content.options!.filter((_, i) => i !== optionIndex)
                        setSections(newSections)
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <input
                    className="input input-sm"
                    value={option.text}
                    onChange={(e) => {
                      const newSections = [...sections]
                      newSections[sectionIndex].steps[stepIndex].content.options![optionIndex].text = e.target.value
                      setSections(newSections)
                    }}
                    placeholder="Option text"
                  />
                  <input
                    className="input input-sm"
                    value={option.feedback}
                    onChange={(e) => {
                      const newSections = [...sections]
                      newSections[sectionIndex].steps[stepIndex].content.options![optionIndex].feedback = e.target.value
                      setSections(newSections)
                    }}
                    placeholder="Feedback"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ContentBlockEditor removed */}
      </div>
    )
  }

  return (
    <div className="editor-panel">
      <div className="editor-section">
        <h3 className="editor-section-title">Lesson Settings</h3>
        <div className="editor-field">
          <label>Title</label>
          <input
            className="input"
            value={gameConfig.title}
            onChange={(e) => setGameConfig({ ...gameConfig, title: e.target.value })}
            placeholder="Lesson title"
          />
        </div>
        <div className="editor-field">
          <label>Description</label>
          <textarea
            className="textarea"
            value={gameConfig.description}
            onChange={(e) => setGameConfig({ ...gameConfig, description: e.target.value })}
            placeholder="Lesson description"
            rows={3}
          />
        </div>

        <div className="editor-field">
          <label>Icon Image (Game Card)</label>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <input
              className="input"
              value={gameConfig.iconImage || ""}
              onChange={(e) => setGameConfig({ ...gameConfig, iconImage: e.target.value })}
              placeholder="/content-beta/nl/lesson-id/images/icon.png"
              style={{ flex: 1 }}
            />
            <label className="btn-sm btn-secondary" style={{ cursor: "pointer", margin: 0 }}>
              {uploadingIcon ? "Uploading..." : "Upload Image"}
              <input
                type="file"
                accept=".png,.jpg,.jpeg,.gif,.webp"
                style={{ display: "none" }}
                disabled={uploadingIcon}
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) {
                    handleIconUpload(file)
                  }
                }}
              />
            </label>
          </div>
          {gameConfig.iconImage && (
            <div style={{ marginTop: "8px" }}>
              <img
                src={gameConfig.iconImage || "/placeholder.svg"}
                alt="Icon preview"
                style={{ maxWidth: "100px", maxHeight: "100px", borderRadius: "8px", border: "1px solid #ddd" }}
              />
            </div>
          )}
        </div>

        <div className="editor-field-row">
          <div className="editor-field">
            <label>Emoji</label>
            <input
              className="input"
              value={gameConfig.emoji}
              onChange={(e) => setGameConfig({ ...gameConfig, emoji: e.target.value })}
            />
          </div>
          <div className="editor-field">
            <label>Difficulty</label>
            <select
              className="select"
              value={gameConfig.difficulty}
              onChange={(e) => setGameConfig({ ...gameConfig, difficulty: e.target.value })}
            >
              <option>Beginner</option>
              <option>Intermediate</option>
              <option>Advanced</option>
            </select>
          </div>
          <div className="editor-field">
            <label>Avatar</label>
            <select
              className="select"
              value={gameConfig.avatar || "max"}
              onChange={(e) => {
                setGameConfig({ ...gameConfig, avatar: e.target.value })
                if (e.target.value !== "bella") {
                  setBellaPreset("custom")
                }
              }}
            >
              <option value="max">Max</option>
              <option value="bella">Bella</option>
            </select>
          </div>
        </div>

        <div className="editor-field-row">
          <div className="editor-field">
            <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={gameConfig.comingSoon || false}
                onChange={(e) => setGameConfig({ ...gameConfig, comingSoon: e.target.checked })}
              />
              Coming Soon
            </label>
          </div>
          <div className="editor-field">
            <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={gameConfig.locked || false}
                onChange={(e) => setGameConfig({ ...gameConfig, locked: e.target.checked })}
              />
              Locked
            </label>
          </div>
        </div>

        {gameConfig.avatar === "bella" && (
          <div className="editor-field">
            <label>Bella Position Preset</label>
            <select className="select" value={bellaPreset} onChange={(e) => applyBellaPreset(e.target.value)}>
              <option value="right">Right Aligned</option>
              <option value="centered">Centered</option>
              <option value="custom">Custom</option>
            </select>
          </div>
        )}

        <div className="editor-field">
          <button
            className="btn-sm btn-outline"
            onClick={() => setShowLessonAvatarSettings(!showLessonAvatarSettings)}
            style={{ width: "100%", justifyContent: "space-between", display: "flex", alignItems: "center" }}
          >
            <span>
              {gameConfig.avatar === "bella" && bellaPreset !== "custom"
                ? "Advanced Settings"
                : "Avatar Position Settings"}
            </span>
            {showLessonAvatarSettings ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>

        {showLessonAvatarSettings && (
          <div className="avatar-settings-panel">
            <div className="editor-field-row">
              <div className="editor-field">
                <label>Alignment</label>
                <select
                  className="select"
                  value={gameConfig.avatarAlignment || "right"}
                  onChange={(e) => setGameConfig({ ...gameConfig, avatarAlignment: e.target.value })}
                >
                  <option value="left">Left</option>
                  <option value="right">Right</option>
                  <option value="centered">Centered</option>
                </select>
              </div>
              <div className="editor-field">
                <label>Scale</label>
                <input
                  type="number"
                  className="input"
                  value={gameConfig.avatarScale || 1}
                  onChange={(e) =>
                    setGameConfig({ ...gameConfig, avatarScale: Number.parseFloat(e.target.value) || 1 })
                  }
                  step="0.1"
                  min="0.1"
                  max="3"
                />
              </div>
              <div className="editor-field">
                <label>Mobile Scale</label>
                <input
                  type="number"
                  className="input"
                  value={gameConfig.mobileScale || 1}
                  onChange={(e) =>
                    setGameConfig({ ...gameConfig, mobileScale: Number.parseFloat(e.target.value) || 1 })
                  }
                  step="0.1"
                  min="0.1"
                  max="3"
                />
              </div>
            </div>

            <div className="editor-field">
              <label>Desktop Position (CSS values like "20px", "10%", "5rem", etc.)</label>
              <div className="editor-field-row">
                <div className="editor-field">
                  <label style={{ fontSize: "12px" }}>Top</label>
                  <input
                    className="input input-sm"
                    value={gameConfig.top || ""}
                    onChange={(e) => setGameConfig({ ...gameConfig, top: e.target.value })}
                    placeholder="auto"
                  />
                </div>
                <div className="editor-field">
                  <label style={{ fontSize: "12px" }}>Bottom</label>
                  <input
                    className="input input-sm"
                    value={gameConfig.bottom || ""}
                    onChange={(e) => setGameConfig({ ...gameConfig, bottom: e.target.value })}
                    placeholder="auto"
                  />
                </div>
                <div className="editor-field">
                  <label style={{ fontSize: "12px" }}>Left</label>
                  <input
                    className="input input-sm"
                    value={gameConfig.left || ""}
                    onChange={(e) => setGameConfig({ ...gameConfig, left: e.target.value })}
                    placeholder="auto"
                  />
                </div>
                <div className="editor-field">
                  <label style={{ fontSize: "12px" }}>Right</label>
                  <input
                    className="input input-sm"
                    value={gameConfig.right || ""}
                    onChange={(e) => setGameConfig({ ...gameConfig, right: e.target.value })}
                    placeholder="auto"
                  />
                </div>
              </div>
            </div>

            <div className="editor-field">
              <label>Mobile Position</label>
              <div className="editor-field-row">
                <div className="editor-field">
                  <label style={{ fontSize: "12px" }}>Mobile Right</label>
                  <input
                    className="input input-sm"
                    value={gameConfig.mobileRight || ""}
                    onChange={(e) => setGameConfig({ ...gameConfig, mobileRight: e.target.value })}
                    placeholder="auto"
                  />
                </div>
                <div className="editor-field">
                  <label style={{ fontSize: "12px" }}>Mobile Bottom</label>
                  <input
                    className="input input-sm"
                    value={gameConfig.mobileBottom || ""}
                    onChange={(e) => setGameConfig({ ...gameConfig, mobileBottom: e.target.value })}
                    placeholder="auto"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="editor-section">
        <div className="editor-section-header">
          <h3 className="editor-section-title">Sections & Steps</h3>
          <button className="btn-sm btn-primary" onClick={addSection}>
            <Plus size={14} />
            Section
          </button>
        </div>

        <div className="sections-list">
          {sections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="section-item">
              <div className="section-item-header">
                <button className="btn-icon" onClick={() => toggleSection(sectionIndex)}>
                  {expandedSections.has(sectionIndex) ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
                <input
                  type="text"
                  className="section-emoji-input"
                  value={section.emoji}
                  onChange={(e) => updateSectionEmoji(sectionIndex, e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  maxLength={2}
                  style={{
                    width: "32px",
                    textAlign: "center",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    padding: "2px",
                    fontSize: "16px",
                  }}
                />
                <input
                  type="text"
                  className="section-title-input"
                  value={section.title}
                  onChange={(e) => updateSectionTitle(sectionIndex, e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  placeholder="Section Title"
                  style={{
                    flex: 1,
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    padding: "4px 8px",
                    fontSize: "14px",
                  }}
                />
                <button className="btn-icon" onClick={() => deleteSection(sectionIndex)}>
                  <Trash2 size={14} />
                </button>
              </div>

              {expandedSections.has(sectionIndex) && (
                <div className="section-content">
                  <div className="section-avatar-settings">
                    <button
                      className="btn-sm btn-outline"
                      onClick={() => toggleSectionAvatarSettings(sectionIndex)}
                      style={{
                        width: "100%",
                        justifyContent: "space-between",
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "12px",
                      }}
                    >
                      <span>Section Avatar Override</span>
                      {expandedSectionAvatarSettings.has(sectionIndex) ? (
                        <ChevronUp size={14} />
                      ) : (
                        <ChevronDown size={14} />
                      )}
                    </button>

                    {expandedSectionAvatarSettings.has(sectionIndex) && (
                      <div className="avatar-settings-panel" style={{ marginBottom: "16px" }}>
                        <div className="editor-field-row">
                          <div className="editor-field">
                            <label>Avatar</label>
                            <select
                              className="select"
                              value={section.config?.avatar || ""}
                              onChange={(e) => {
                                updateSectionConfig(sectionIndex, "avatar", e.target.value || undefined)
                                if (e.target.value !== "bella") {
                                  const newPresets = new Map(sectionBellaPresets)
                                  newPresets.delete(sectionIndex)
                                  setSectionBellaPresets(newPresets)
                                }
                              }}
                            >
                              <option value="">Use Lesson Default</option>
                              <option value="max">Max</option>
                              <option value="bella">Bella</option>
                            </select>
                          </div>
                        </div>

                        {section.config?.avatar === "bella" && (
                          <div className="editor-field">
                            <label>Bella Position Preset</label>
                            <select
                              className="select"
                              value={sectionBellaPresets.get(sectionIndex) || "custom"}
                              onChange={(e) => applySectionBellaPreset(sectionIndex, e.target.value)}
                            >
                              <option value="right">Right Aligned</option>
                              <option value="centered">Centered</option>
                              <option value="custom">Custom</option>
                            </select>
                          </div>
                        )}

                        {(section.config?.avatar !== "bella" ||
                          sectionBellaPresets.get(sectionIndex) === "custom" ||
                          !sectionBellaPresets.has(sectionIndex)) && (
                          <>
                            <div className="editor-field-row">
                              <div className="editor-field">
                                <label>Alignment</label>
                                <select
                                  className="select"
                                  value={section.config?.avatarAlignment || ""}
                                  onChange={(e) =>
                                    updateSectionConfig(sectionIndex, "avatarAlignment", e.target.value || undefined)
                                  }
                                >
                                  <option value="">Use Lesson Default</option>
                                  <option value="left">Left</option>
                                  <option value="right">Right</option>
                                  <option value="centered">Centered</option>
                                </select>
                              </div>
                            </div>

                            <div className="editor-field-row">
                              <div className="editor-field">
                                <label>Scale</label>
                                <input
                                  type="number"
                                  className="input"
                                  value={section.config?.avatarScale || ""}
                                  onChange={(e) =>
                                    updateSectionConfig(
                                      sectionIndex,
                                      "avatarScale",
                                      e.target.value ? Number.parseFloat(e.target.value) : undefined,
                                    )
                                  }
                                  step="0.1"
                                  min="0.1"
                                  max="3"
                                  placeholder="Default"
                                />
                              </div>
                              <div className="editor-field">
                                <label>Mobile Scale</label>
                                <input
                                  type="number"
                                  className="input"
                                  value={section.config?.mobileScale || ""}
                                  onChange={(e) =>
                                    updateSectionConfig(
                                      sectionIndex,
                                      "mobileScale",
                                      e.target.value ? Number.parseFloat(e.target.value) : undefined,
                                    )
                                  }
                                  step="0.1"
                                  min="0.1"
                                  max="3"
                                  placeholder="Default"
                                />
                              </div>
                            </div>

                            <div className="editor-field">
                              <label>Desktop Position</label>
                              <div className="editor-field-row">
                                <div className="editor-field">
                                  <label style={{ fontSize: "12px" }}>Top</label>
                                  <input
                                    className="input input-sm"
                                    value={section.config?.top || ""}
                                    onChange={(e) =>
                                      updateSectionConfig(sectionIndex, "top", e.target.value || undefined)
                                    }
                                    placeholder="auto"
                                  />
                                </div>
                                <div className="editor-field">
                                  <label style={{ fontSize: "12px" }}>Bottom</label>
                                  <input
                                    className="input input-sm"
                                    value={section.config?.bottom || ""}
                                    onChange={(e) =>
                                      updateSectionConfig(sectionIndex, "bottom", e.target.value || undefined)
                                    }
                                    placeholder="auto"
                                  />
                                </div>
                                <div className="editor-field">
                                  <label style={{ fontSize: "12px" }}>Left</label>
                                  <input
                                    className="input input-sm"
                                    value={section.config?.left || ""}
                                    onChange={(e) =>
                                      updateSectionConfig(sectionIndex, "left", e.target.value || undefined)
                                    }
                                    placeholder="auto"
                                  />
                                </div>
                                <div className="editor-field">
                                  <label style={{ fontSize: "12px" }}>Right</label>
                                  <input
                                    className="input input-sm"
                                    value={section.config?.right || ""}
                                    onChange={(e) =>
                                      updateSectionConfig(sectionIndex, "right", e.target.value || undefined)
                                    }
                                    placeholder="auto"
                                  />
                                </div>
                              </div>
                            </div>

                            <div className="editor-field">
                              <label>Mobile Position</label>
                              <div className="editor-field-row">
                                <div className="editor-field">
                                  <label style={{ fontSize: "12px" }}>Mobile Right</label>
                                  <input
                                    className="input input-sm"
                                    value={section.config?.mobileRight || ""}
                                    onChange={(e) =>
                                      updateSectionConfig(sectionIndex, "mobileRight", e.target.value || undefined)
                                    }
                                    placeholder="auto"
                                  />
                                </div>
                                <div className="editor-field">
                                  <label style={{ fontSize: "12px" }}>Mobile Bottom</label>
                                  <input
                                    className="input input-sm"
                                    value={section.config?.mobileBottom || ""}
                                    onChange={(e) =>
                                      updateSectionConfig(sectionIndex, "mobileBottom", e.target.value || undefined)
                                    }
                                    placeholder="auto"
                                  />
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="steps-list">
                    {section.steps.map((step, stepIndex) => {
                      const isExpanded = currentSectionIndex === sectionIndex && currentStepIndex === stepIndex
                      const isActive = currentSectionIndex === sectionIndex && currentStepIndex === stepIndex

                      return (
                        <div key={stepIndex} className="step-item-wrapper">
                          <div
                            className={`step-item ${isActive ? "step-item-active" : ""}`}
                            onClick={() => {
                              setCurrentSectionIndex(sectionIndex)
                              setCurrentStepIndex(stepIndex)
                            }}
                          >
                            <button
                              className="btn-icon"
                              onClick={(e) => {
                                e.stopPropagation()
                                if (isExpanded) {
                                  // Don't collapse, just keep it expanded
                                } else {
                                  setCurrentSectionIndex(sectionIndex)
                                  setCurrentStepIndex(stepIndex)
                                }
                              }}
                            >
                              {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            </button>
                            <span className="step-number">{stepIndex + 1}</span>
                            <span className="step-title">{step.content.title || "Untitled"}</span>
                            <span className="step-type-badge">{step.type}</span>
                            <button
                              className="btn-icon"
                              onClick={(e) => {
                                e.stopPropagation()
                                deleteStep(sectionIndex, stepIndex)
                              }}
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                          {isExpanded && renderStepEditor(step, sectionIndex, stepIndex)}
                        </div>
                      )
                    })}
                  </div>
                  <div className="add-step-buttons">
                    <button className="btn-sm btn-outline" onClick={() => addStep(sectionIndex, "story")}>
                      <Plus size={12} />
                      Story
                    </button>
                    <button className="btn-sm btn-outline" onClick={() => addStep(sectionIndex, "quiz")}>
                      <Plus size={12} />
                      Quiz
                    </button>
                    <button className="btn-sm btn-outline" onClick={() => addStep(sectionIndex, "video")}>
                      <Plus size={12} />
                      Video
                    </button>
                    <button className="btn-sm btn-outline" onClick={() => addStep(sectionIndex, "celebration")}>
                      <Plus size={12} />
                      Celebration
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
